import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { firmInfo, navLinks } from '@/mocks/firm';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  const solid = scrolled || open || pathname !== '/';

  const isActive = (href: string) => {
    const [rawPath, rawHash] = href.split('#');
    const linkPath = rawPath || '/';
    const linkHash = rawHash ? `#${rawHash}` : '';
    return pathname === linkPath && hash === linkHash;
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${solid ? 'bg-background-50 border-b border-background-200' : 'bg-transparent'}`}>
      <nav className="w-full px-4 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className={`flex items-center gap-3 cursor-pointer ${solid ? 'text-foreground-950' : 'text-background-50'}`}>
          <span className="w-10 h-10 rounded-full border border-accent-500 flex items-center justify-center text-accent-500">
            <i className="ri-scales-3-line text-lg"></i>
          </span>
          <span className="font-heading text-2xl font-semibold tracking-wide">{firmInfo.name}</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${isActive(l.href) ? 'text-accent-500' : solid ? 'text-foreground-800 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'}`}
            >
              {l.label}
            </Link>
          ))}
          <a href={`tel:${firmInfo.phone}`} className="bg-[rgb(37,44,89)] hover:bg-[rgb(28,33,68)] text-background-50 dark:text-foreground-950 text-sm px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer flex items-center gap-2">
            <i className="ri-phone-line"></i>{firmInfo.phone}
          </a>
        </div>
        <button type="button" onClick={() => setOpen(!open)} className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer whitespace-nowrap ${solid ? 'text-foreground-950' : 'text-background-50'}`} aria-label="Toggle menu">
          <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </nav>
      {open && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 flex flex-col gap-1 max-h-[calc(100vh-5rem)] overflow-y-auto">
          {navLinks.map((l) => (
            <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className={`py-3 text-sm cursor-pointer ${isActive(l.href) ? 'text-accent-600' : 'text-foreground-800'}`}>
              {l.label}
            </Link>
          ))}
          <a href={`tel:${firmInfo.phone}`} className="mt-2 bg-primary-500 text-background-50 text-sm px-5 py-3 rounded-md text-center whitespace-nowrap cursor-pointer">Call {firmInfo.phone}</a>
        </div>
      )}
    </header>
  );
}